import { createInstance, enums, OptimizelyDecideOption, } from '@optimizely/optimizely-sdk';
import * as Logger from '@thoughtspot/logger';
import _ from 'lodash';
import { TrackingKeys } from './experimentation.utils';
import { onDecision } from './experimentation-notification-listners';
const logger = Logger.create('optimizely-service');
export class Experimenter {
    constructor(config) {
        this.updateInterval = 300000;
        this.forcedExperimentKey = '';
        this.forcedVariationKey = '';
        this.optimizelyClientInstance = null;
        this.enabled = false;
        this.isDebugModeOn = false;
        this.checkAndSetForcedVariation = () => {
            if (!!this.optimizelyClientInstance &&
                this.forcedExperimentKey !== '' &&
                this.forcedVariationKey !== '') {
                this.optimizelyClientInstance.setForcedVariation(this.forcedExperimentKey, this.userGuid, this.forcedVariationKey);
                logger.debug(`OPTIMIZELY [${this.project}] : ${this.forcedExperimentKey} has forced variation ${this.forcedVariationKey}`);
            }
        };
        this.accessToken = config.accessToken;
        this.userGuid = config.userGuid;
        this.clusterId = config.clusterId;
        this.project = config.project;
        this.updateInterval = config.updateInterval || 300000;
        this.forcedExperimentKey = config.forcedExperimentKey || '';
        this.forcedVariationKey = config.forcedVariationKey || '';
        this.enabled = config.enabled;
        this.isDebugModeOn = !!config.isDebugModeOn;
        this.orgId = config.orgId;
        if (this.enabled) {
            this.optimizelyClientInstance = createInstance({
                sdkKey: this.accessToken,
                logLevel: this.isDebugModeOn
                    ? enums.LOG_LEVEL.DEBUG
                    : enums.LOG_LEVEL.ERROR,
                datafileOptions: {
                    autoUpdate: true,
                    updateInterval: this.updateInterval,
                },
            });
            this.userContext = this.optimizelyClientInstance.createUserContext(this.userGuid, {
                clusterId: this.clusterId,
                orgId: this.orgId,
            });
            this.optimizelyClientInstance
                .onReady()
                .then(() => {
                return this.optimizelyClientInstance.notificationCenter.addNotificationListener(enums.NOTIFICATION_TYPES.DECISION, onDecision);
            })
                .catch(() => _.noop);
        }
    }
    getOptimizelyDecisionObject(featureKey) {
        if (this.userContext) {
            this.checkAndSetForcedVariation();
            const decisionObject = this.userContext.decide(featureKey, [OptimizelyDecideOption.ENABLED_FLAGS_ONLY]);
            this.checkAndClearForcedVariation();
            return decisionObject;
        }
        return undefined;
    }
    getOptimizelyClientInstance() {
        return this.optimizelyClientInstance;
    }
    isFeatureEnabled(featureKey) {
        const decisionObject = this.getOptimizelyDecisionObject(featureKey);
        if (decisionObject) {
            logger.debug(`OPTIMIZELY [${this.project}] : feature ${featureKey} is ${decisionObject.enabled} for ${this.userGuid}`);
            return decisionObject.enabled;
        }
        logger.debug(`OPTIMIZELY [${this.project}] : not initialized.`);
        return undefined;
    }
    trackEvent(event) {
        if (this.enabled) {
            logger.debug(`OPTIMIZELY [${this.project}] : tracking event ${event} for ${this.userGuid}`);
            const eventTags = {
                [TrackingKeys.PROJECT_NAME]: this.project,
            };
            this.userContext.trackEvent(event, eventTags);
            return true;
        }
        logger.debug(`OPTIMIZELY [${this.project}] : not initialized.`);
        return undefined;
    }
    getFeatureVariableBoolean(featureKey, variableKey) {
        const decisionObject = this.getOptimizelyDecisionObject(featureKey);
        if (decisionObject) {
            const featureVariableBoolean = decisionObject.variables[variableKey];
            logger.debug(`OPTIMIZELY [${this.project}] : ${featureKey} has variable ${variableKey} with value ${featureVariableBoolean} for ${this.userGuid}`);
            return featureVariableBoolean;
        }
        logger.debug(`OPTIMIZELY [${this.project}] : not initialized.`);
        return undefined;
    }
    getFeatureVariableString(featureKey, variableKey) {
        const decisionObject = this.getOptimizelyDecisionObject(featureKey);
        if (decisionObject) {
            const featureVariableString = decisionObject.variables[variableKey];
            logger.debug(`OPTIMIZELY [${this.project}] : ${featureKey} has variable ${variableKey} with value ${featureVariableString} for ${this.userGuid}`);
            return featureVariableString;
        }
        logger.debug(`OPTIMIZELY [${this.project}] : not initialized.`);
        return undefined;
    }
    getFeatureVariableNumber(featureKey, variableKey) {
        const decisionObject = this.getOptimizelyDecisionObject(featureKey);
        if (decisionObject) {
            const featureVariableNummber = decisionObject.variables[variableKey];
            logger.debug(`OPTIMIZELY [${this.project}] : ${featureKey} has variable ${variableKey} with value ${featureVariableNummber} for ${this.userGuid}`);
            return featureVariableNummber;
        }
        logger.debug(`OPTIMIZELY [${this.project}] : not initialized.`);
        return undefined;
    }
    getFeatureVariableJson(featureKey, variableKey) {
        const decisionObject = this.getOptimizelyDecisionObject(featureKey);
        if (decisionObject) {
            const featureVariableObject = decisionObject.variables[variableKey];
            logger.debug(`OPTIMIZELY [${this.project}] : ${featureKey} has variable ${variableKey} with value ${featureVariableObject} for ${this.userGuid}`);
            return featureVariableObject;
        }
        logger.debug(`OPTIMIZELY [${this.project}] : not initialized.`);
        return undefined;
    }
    checkAndClearForcedVariation() {
        if (!!this.optimizelyClientInstance &&
            this.forcedExperimentKey !== '' &&
            this.forcedVariationKey !== '') {
            this.optimizelyClientInstance.setForcedVariation(this.forcedExperimentKey, this.userGuid, null);
            logger.debug(`OPTIMIZELY [${this.project}] : ${this.forcedExperimentKey} forced variation ${this.forcedVariationKey} cleared.`);
        }
    }
    checkIfForcedVariation() {
        return (this.forcedExperimentKey !== '' && this.forcedVariationKey !== '');
    }
}
//# sourceMappingURL=experimenter.js.map